import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaQuoteLeft, FaArrowLeft, FaArrowRight } from 'react-icons/fa';

interface Testimonial {
  id: string;
  quote: string;
  name: string;
  position: string;
  company: string;
  image?: string;
  websiteUrl?: string;
  websiteImage?: string;
}

interface TestimonialsProps {
  title?: string;
  subtitle?: string;
  testimonials: Testimonial[];
}

const TestimonialsContainer = styled.section`
  padding: 5rem 1rem;
  background-color: #fff;
`;

const TestimonialsHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
`;

const TestimonialsTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const TestimonialsSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--gray);
  margin-bottom: 2rem;
`;

const TestimonialsSlider = styled.div`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
`;

const TestimonialCard = styled.div`
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: 3rem;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 3rem;
  }
`;

const QuoteContainer = styled.div`
  flex: 3;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    margin-bottom: 0;
  }
`;

const QuoteIcon = styled.div`
  font-size: 2.5rem;
  color: var(--blue);
  opacity: 0.2;
  margin-bottom: 1rem;
`;

const QuoteText = styled.p`
  font-size: 1.25rem;
  line-height: 1.7;
  color: var(--gray-dark);
  font-style: italic;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 1.35rem;
  }
`;

const AuthorContainer = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  @media (min-width: 768px) {
    align-items: flex-start;
    text-align: left;
  }
`;

const AuthorImage = styled.div<{ image?: string }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin-bottom: 1rem;
  background-image: ${props => props.image ? `url("${props.image}")` : 'linear-gradient(45deg, #f0f0f0, #e0e0e0)'};
  background-size: cover;
  background-position: center;
`;

const AuthorName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const AuthorPosition = styled.p`
  font-size: 0.95rem;
  color: var(--gray);
  margin-bottom: 0.25rem;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const NavButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: white;
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: var(--gray-dark);
  
  &:hover {
    background-color: var(--blue);
    border-color: var(--blue);
    color: white;
  }
  
  &:disabled {
    background-color: #f0f0f0;
    border-color: #e0e0e0;
    color: #c0c0c0;
    cursor: not-allowed;
  }
`;

const Testimonials: React.FC<TestimonialsProps> = ({
  title = "Lo que dicen nuestros clientes",
  subtitle = "Descubre por qué muchos profesionales y empresas ya confían en nosotros",
  testimonials
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    // Log para depuración
    console.log('Testimonials cargados:', testimonials);
    console.log('Ruta de imagen del testimonio actual:', testimonials[currentIndex]?.image);
  }, [testimonials, currentIndex]);
  
  const goToPrevious = () => {
    setCurrentIndex(current => (current === 0 ? testimonials.length - 1 : current - 1));
  };
  
  const goToNext = () => {
    setCurrentIndex(current => (current === testimonials.length - 1 ? 0 : current + 1));
  };
  
  const currentTestimonial = testimonials[currentIndex];
  
  return (
    <TestimonialsContainer>
      <TestimonialsHeader>
        <TestimonialsTitle>{title}</TestimonialsTitle>
        {subtitle && <TestimonialsSubtitle>{subtitle}</TestimonialsSubtitle>}
      </TestimonialsHeader>
      
      <TestimonialsSlider>
        <TestimonialCard>
          <QuoteContainer>
            <QuoteIcon>
              <FaQuoteLeft />
            </QuoteIcon>
            <QuoteText>{currentTestimonial.quote}</QuoteText>
          </QuoteContainer>
          
          <AuthorContainer>
            <AuthorImage 
              image={currentTestimonial.image} 
              data-img-path={currentTestimonial.image}
              style={{
                backgroundImage: currentTestimonial.image ? `url("${currentTestimonial.image}")` : 'linear-gradient(45deg, #f0f0f0, #e0e0e0)'
              }}
            />
            <AuthorName>{currentTestimonial.name}</AuthorName>
            <AuthorPosition>
              {currentTestimonial.position} | {currentTestimonial.company}
            </AuthorPosition>
          </AuthorContainer>
        </TestimonialCard>
        
        <NavigationButtons>
          <NavButton 
            onClick={goToPrevious} 
            disabled={testimonials.length <= 1}
          >
            <FaArrowLeft />
          </NavButton>
          <NavButton 
            onClick={goToNext}
            disabled={testimonials.length <= 1}
          >
            <FaArrowRight />
          </NavButton>
        </NavigationButtons>
      </TestimonialsSlider>
    </TestimonialsContainer>
  );
};

export default Testimonials; 